import type { MovingRect } from './canvasAlignment'
import { SPAWN_GAP } from './canvasSpawn'
import { getMemoColor } from './canvasMemoStyle'
import { DEFAULT_CANVAS_NOTE_WIDTH, DEFAULT_CANVAS_NOTE_HEIGHT, MIN_CANVAS_NOTE_SIZE } from './canvasNoteStyle'

/**
 * Der **Bereich** (CONTEXT.md; ADR 0012) — das Rechenwerk hinter der
 * Zugehörigkeit. Reines Rechenwerk ohne React, wie die übrigen `utils/`.
 *
 * **Ein Zettel gehört zu genau einem Bereich oder zu keinem.** Entschieden
 * wird beim Loslassen: Es gewinnt der Bereich, der **den größeren Teil des
 * Zettels bedeckt**, mindestens aber **die Hälfte**. Bei Gleichstand (der
 * Zettel liegt ganz in zwei übereinanderliegenden Bereichen) gewinnt der
 * **obere** — dieselbe Regel wie beim **Tauschpunkt** („liegen mehrere
 * übereinander, gewinnt das oberste"): Die Einträge kommen in
 * Stapelreihenfolge, der letzte Treffer liegt oben.
 *
 * Gemessen wird gegen die **Karte**, nicht gegen den Reiter — sonst zöge ein
 * Name Zettel herein.
 */

/** Ein Bereich, wie er in die Rechnung geht — in Stapelreihenfolge, der letzte liegt oben. */
export interface AreaEntry {
  id: string
  rect: MovingRect
}

/** Der Innenabstand eines Bereichs: ein **Takt**, wie zwischen zwei Zetteln. */
export const AREA_PADDING = SPAWN_GAP

/** Standardgröße beim einfachen Klick: Platz für 3×2 Zettel (Grillen 2026-09-04). */
export const DEFAULT_AREA_WIDTH = 3 * DEFAULT_CANVAS_NOTE_WIDTH + 2 * SPAWN_GAP + 2 * AREA_PADDING
export const DEFAULT_AREA_HEIGHT = 2 * DEFAULT_CANVAS_NOTE_HEIGHT + SPAWN_GAP + 2 * AREA_PADDING

/** Höhe des Reiters über der Karte (Figma `Section` 5136:95471). */
export const AREA_TAB_HEIGHT = 34

/**
 * Mindestmaß eines Bereichs: **so klein wie der kleinste Zettel**
 * (`MIN_CANVAS_NOTE_SIZE`, gewünscht 2026-09-08). Keine Höchstgröße.
 *
 * Zwei Untergrenzen im selben Bild wären eine zu viel — was als **Notiz** noch
 * geht, muss auch als Bereich gehen, und ein Bereich ist ja „ein
 * **Auswahlrahmen**, der bleibt": Er darf auch einen einzigen kleinen Zettel
 * fassen oder als Merker leer dastehen. (Bis dahin war es eine ganze
 * Zettelbreite plus zweimal **Takt** — gedacht als „ein Zettel plus
 * Innenabstand passt hinein", in der Hand aber eine Grenze, die schon bei
 * kleinen Ordnungen im Weg stand.)
 *
 * Liegt auf dem Punktraster (72 = 3 × 24), wie die Grenze am Zettel: Sonst
 * landete ein Bereich, den man bis an die Grenze zieht, genau dort neben dem
 * Raster.
 */
export const MIN_AREA_SIZE = MIN_CANVAS_NOTE_SIZE

/** Wie viel von `rect` unter `area` liegt — als Anteil seiner Fläche, 0…1. */
export function coverageOf(rect: MovingRect, area: MovingRect): number {
  const w = Math.min(rect.x + rect.width, area.x + area.width) - Math.max(rect.x, area.x)
  const h = Math.min(rect.y + rect.height, area.y + area.height) - Math.max(rect.y, area.y)
  if (w <= 0 || h <= 0) return 0
  const own = rect.width * rect.height
  return own > 0 ? (w * h) / own : 0
}

/**
 * Welcher Bereich `rect` aufnähme — oder `null`, wenn keiner mehr als die
 * Hälfte bedeckt. Bei Gleichstand der obere (der spätere in der Liste).
 */
export function areaFor(rect: MovingRect, areas: readonly AreaEntry[]): AreaEntry | null {
  let best: AreaEntry | null = null
  let most = 0.5
  for (const area of areas) {
    const share = coverageOf(rect, area.rect)
    // `>=` beim Anteil: Gleichstand fällt an den späteren, also oberen.
    if (share > 0.5 && share >= most) {
      best = area
      most = share
    }
  }
  return best
}

/**
 * Die Lage, in der ein Bereich seine Mitglieder **eng umfasst** — ein Takt
 * Luft rundum. Ohne Mitglieder `null`: Ein leerer Bereich bleibt, wie er ist.
 */
export function fitAround(members: readonly MovingRect[]): MovingRect | null {
  if (members.length === 0) return null
  const pad = AREA_PADDING
  const left = Math.min(...members.map(m => m.x)) - pad
  const top = Math.min(...members.map(m => m.y)) - pad
  const right = Math.max(...members.map(m => m.x + m.width)) + pad
  const bottom = Math.max(...members.map(m => m.y + m.height)) + pad
  return {
    x: left,
    y: top,
    width: Math.max(right - left, MIN_AREA_SIZE),
    height: Math.max(bottom - top, MIN_AREA_SIZE),
  }
}

/** Die drei Stufen einer Farbfamilie, wie der Bereich sie trägt. */
export interface AreaColor {
  /** Füllung der Karte und des Reiters. */
  surface: string
  /** Die Haarlinie rundum. */
  border: string
  /** Die Schrift im Reiter. */
  text: string
}

/**
 * Jede Farbe nimmt drei Stufen aus der Palette der **Notiz** — hellste als
 * Füllung, eine dunkler als Kontur, dunkelste als Schrift. Dieselbe Palette
 * wie beim Zettel, nur blasser: Ein Bereich in kräftigem Gelb wäre ein
 * Riesen-Zettel.
 *
 * **Ohne eigene Farbe gilt Gelb** — derselbe Ton, den der Farbwähler an
 * erster Stelle führt (`DEFAULT_CANVAS_NOTE_COLOR`, über den Rückfall in
 * `getMemoColor`). Das Figma-Design zeigt einen eigenen Cremeton
 * (`#FFFBF0`/`#FFECBD`); er stand bis 2026-09-05 hier und ist bewusst
 * aufgegeben: Eine Vorgabe, die in keinem Feld des Wählers steht, lässt sich
 * nach dem ersten Farbwechsel nicht wiederherstellen.
 */
export function getAreaColor(key: string | null | undefined): AreaColor {
  const c = getMemoColor(key ?? null)
  return { surface: c.surface, border: c.border, text: c.text }
}
